import axios from 'axios';
import toast from 'react-hot-toast';
import { CONFIG } from '../config';
import { getAuthToken, getCompanyReferer } from '../utils/cookieUtils';
import { generateRefererUrl } from '../utils/urlParser';
import { navigateToAccessDenied, navigateToLogin } from '../utils/navigationUtils';

const baseURL = `${CONFIG.BASE_URL}`;

const createAxiosInstance = (endpoint, responseType, headers) => {
    const instance = axios.create({
        baseURL: `${baseURL}/${endpoint}`,
        responseType: responseType,
        headers: {
            'Content-Type': 'application/json',
            appType: 'hq-ui',
            ...headers,
        },
    });

    instance.interceptors.request.use((req) => {
        const token = getAuthToken();
        const companyReferer = getCompanyReferer();
        if (token) req.headers.Authorization = `Bearer ${token}`;
        if (companyReferer) {
            const refererUrl = generateRefererUrl(companyReferer);
            if (refererUrl) {
                req.headers['X-Referer'] = refererUrl;
            }
        }
        return req;
    });

    instance.interceptors.response.use(
        (res) => {
            if (typeof res.data === 'string') {
                try {
                    res.data = res?.data && JSON.parse(res?.data);
                } catch (e) {
                    console.error('Error parsing response data:', e);
                }
            }

            return res;
        },
        (err) => {
            if (typeof err?.response?.data === 'string') {
                try {
                    err.response.data = JSON.parse(err?.response?.data);
                } catch (e) {
                    console.error('Error parsing response data:', e);
                }
            }

            if (err.response && err.response.status === 403) {
                if (err.response.data.status === 'ERROR' || err.response.data.error === 'Forbidden') {
                    toast.error(err.response.data.message || 'Access Denied');
                } else navigateToAccessDenied();
            } else if (err.response && err.response.status === 401) {
                toast.error(err.response.data.message || 'Authentication Failed');
                navigateToLogin();
            } else return Promise.reject(err);
        },
    );

    return instance;
};

const axiosApiInstance = (routePath) => createAxiosInstance(routePath, 'json');
const axiosFormDataApiInstance = (routePath) =>
    createAxiosInstance(routePath, 'multipart/form-data', {
        'Content-Type': 'multipart/form-data',
    });
const axiosBlobApiInstance = (routePath) =>
    createAxiosInstance(routePath, 'blob', {
        Accept: 'application/octet-stream',
        'Content-Type': 'multipart/form-data',
    });

export const api = axiosApiInstance('api');
export const restAuthApi = axiosFormDataApiInstance('restAuthApi');

export const restApi = axiosApiInstance('restApi');
export const restApiFormData = axiosFormDataApiInstance('restApi');
export const restApiBlob = axiosBlobApiInstance('restApi');

export const restApiV2 = axiosApiInstance('RestApiV2');
export const restApiV2FormData = axiosFormDataApiInstance('RestApiV2');
export const restApiV2Blob = axiosBlobApiInstance('RestApiV2');
